import { type RefObject, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Chart as ChartJS } from 'chart.js';
import classNames from 'classnames';

import {
    buildMetricChartData,
    buildMetricChartOptions,
    MetricChart,
} from '@/components/charts/generic';
import { useChartTheme } from '@/components/charts/theme';
import type { HealthTrendPoint } from '@/domain/health';
import { parseLocale } from '@/utils/localeFormatters';

import {
    buildHealthDevelopmentChartSpec,
    type HealthDevelopmentLineChartDataset,
} from '../../healthDevelopmentChartHelpers';

import styles from './HealthDevelopmentLineChart.module.scss';

interface HealthDevelopmentLineChartProps {
    datasets: HealthDevelopmentLineChartDataset[];
    /** Raw parameter trend for y-axis scaling and tooltip measurement count. */
    parameterTrend?: HealthTrendPoint[];
    /** Plot height in px. Omitted, the plot fills its container (a fixed-height card). */
    chartHeight?: number;
    className?: string;
    isLoading?: boolean;
    /**
     * Renders the title row. Off when a surrounding `ContentBlock` already provides it, as on
     * the Health detail pages.
     */
    showHeader?: boolean;
    /** For the copy / download / fullscreen actions of a surrounding card. */
    chartRef?: RefObject<ChartJS<'line'> | null>;
}

/** The scores are named by the card around the chart, so it draws no legend of its own. */
const NO_LEGEND_ITEMS: never[] = [];

export default function HealthDevelopmentLineChart(props: HealthDevelopmentLineChartProps) {
    const {
        datasets,
        parameterTrend = [],
        className,
        isLoading = false,
        chartHeight,
        showHeader = true,
        chartRef: chartRefProp,
    } = props;
    // The chart always needs a ref; a card with no actions menu passes none.
    const ownChartRef = useRef<ChartJS<'line'>>(null);
    const chartRef = chartRefProp ?? ownChartRef;
    const theme = useChartTheme();

    const { t, i18n } = useTranslation();
    const { t: tHealth } = useTranslation('healthModule');
    const locale = parseLocale(i18n.language);

    const spec = useMemo(
        () => buildHealthDevelopmentChartSpec({ datasets, locale, parameterTrend, t: tHealth }),
        [datasets, locale, parameterTrend, tHealth]
    );
    const chartData = useMemo(() => buildMetricChartData(spec, theme), [spec, theme]);
    const chartOptions = useMemo(() => buildMetricChartOptions(spec, theme), [spec, theme]);
    const visibleSeries = useMemo(() => new Set(spec.series.map((_, index) => index)), [spec]);

    return (
        <div
            className={classNames(
                styles.chartContainer,
                { [styles.fill]: chartHeight == null },
                className
            )}>
            {showHeader ? (
                <div className={styles.header}>
                    <span className={styles.headerLabel}>
                        {t('smoltReport.detailView.developmentTitle')}
                    </span>
                </div>
            ) : null}
            <div className={styles.chartWrapper}>
                <MetricChart
                    kind="line"
                    data={chartData}
                    options={chartOptions}
                    legendItems={NO_LEGEND_ITEMS}
                    visibleSeries={visibleSeries}
                    onToggleSeries={() => undefined}
                    chartRef={chartRef}
                    height={chartHeight ?? 'fill'}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
}
